import { useEffect, useRef, useState } from "react";
import { Button, StickyBottomActions } from "../../../design-v2";
import { REST_DISPLAY } from "../hooks/useRestTimer";
import { MOTION, motionAllowed } from "../motion";

// §14.3 rest surface on the ACTIVE execution screen. Presentation only: it
// renders the display state derived by useRestTimer and calls its controls;
// it never reads the prescription, resolves anchors or writes records.

export function formatRestClock(totalSeconds) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function RestIcon({ name }) {
  const paths = {
    play: <path d="M8 5.5v13l10-6.5-10-6.5Z" />,
    close: <path d="M6 6l12 12M18 6 6 18" />,
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="lz-workout-rest__icon">
      {paths[name]}
    </svg>
  );
}

// The bar slides in when it mounts (CSS) and, where motion is allowed, stays
// mounted for a short exit after the timer is hidden. While it leaves it shows
// its last content and takes no input. Timing semantics are untouched.
export default function RestTimerBar(props) {
  const visible = props.state !== REST_DISPLAY.HIDDEN;
  const lastVisibleRef = useRef(null);
  if (visible) lastVisibleRef.current = props;
  const [present, setPresent] = useState(visible);
  if (visible && !present) setPresent(true);
  if (!visible && present && !motionAllowed()) setPresent(false);
  useEffect(() => {
    if (visible || !present) return undefined;
    const timer = window.setTimeout(() => setPresent(false), MOTION.exit);
    return () => window.clearTimeout(timer);
  }, [visible, present]);
  const leaving = !visible && present && lastVisibleRef.current != null;
  return (
    <RestTimerBarView
      {...(leaving ? lastVisibleRef.current : props)}
      leaving={leaving}
      announceDone={props.state === REST_DISPLAY.DONE}
    />
  );
}

function RestTimerBarView({
  state,
  remainingSeconds,
  durationSeconds,
  canEdit,
  canExtend,
  onPlay,
  onExtend,
  onDismiss,
  leaving,
  announceDone,
}) {
  const running = state === REST_DISPLAY.RUNNING;
  const offering = state === REST_DISPLAY.OFFER;
  const done = state === REST_DISPLAY.DONE;
  const clock = formatRestClock(running ? remainingSeconds : durationSeconds);

  return (
    <>
      {/* Present for the whole ACTIVE screen so "Rest done" is announced once,
          politely, when it appears. The ticking countdown is never live. */}
      <div className="lz-v2-visually-hidden" aria-live="polite" aria-atomic="true">
        {announceDone ? "Rest done" : ""}
      </div>
      {state === REST_DISPLAY.HIDDEN ? null : (
        <StickyBottomActions
          as="aside"
          className={`lz-workout-rest${leaving ? " is-leaving" : ""}`}
          aria-label="Rest timer"
          aria-hidden={leaving || undefined}
          inert={leaving ? "" : undefined}
        >
          <div className="lz-workout-rest__bar" data-state={state.toLowerCase()}>
            <span className="lz-workout-rest__label">
              <span className="lz-workout-rest__dot" aria-hidden="true" />
              {done ? "Rest done" : "Rest"}
            </span>

            {running ? (
              <span
                className="lz-workout-rest__clock"
                role="timer"
                aria-label={`Rest time remaining ${clock}`}
              >
                {clock}
              </span>
            ) : null}
            {offering ? (
              <span className="lz-workout-rest__clock" aria-label={`Rest ${clock}`}>{clock}</span>
            ) : null}

            {running || offering ? (
              // Accessible names begin with the visible label (WCAG 2.5.3).
              <span className="lz-workout-rest__actions">
                {running ? (
                  <Button
                    variant="secondary"
                    className="lz-workout-rest__extend"
                    aria-label="+30s rest"
                    disabled={!canExtend}
                    onClick={onExtend}
                  >
                    +30s
                  </Button>
                ) : (
                  <Button
                    className="lz-workout-rest__play"
                    aria-label={`Play ${clock} rest`}
                    disabled={!canEdit}
                    onClick={onPlay}
                  >
                    <RestIcon name="play" /> Play
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="lz-workout-rest__dismiss"
                  aria-label={running ? "Dismiss rest timer" : "Dismiss rest offer"}
                  disabled={!canEdit}
                  onClick={onDismiss}
                >
                  <RestIcon name="close" />
                </Button>
              </span>
            ) : null}
          </div>
        </StickyBottomActions>
      )}
    </>
  );
}
